// src/components/resources/tools/generators/UtmBuilder/UtmBuilder.tsx

/**
 * @fileoverview UTM Campaign URL Builder
 * @description A free, in-browser builder for tagging campaign destination URLs
 * with Google Analytics / GA4-compatible UTM parameters (utm_source, utm_medium,
 * utm_campaign, utm_term, utm_content).
 *
 * DESIGN NOTES
 * - Fully self-contained client component (no server round-trips). All URL
 *   construction happens locally; nothing is uploaded.
 * - One-click platform presets pre-fill the canonical source/medium pairs media
 *   buyers reach for (Google Ads → google/cpc, Meta → facebook/paid_social, …)
 *   so the tool encodes consistent taxonomy rather than leaving it to ad-hoc
 *   typing — the single biggest source of broken campaign reporting.
 * - "Clean values" (lowercase + spaces→underscores) defaults on because mixed
 *   case and spaces fragment GA reports (Facebook ≠ facebook ≠ FaceBook). It is
 *   a toggle, not a lock, so power users can opt out.
 *
 * @module UtmBuilder
 */

'use client'

import { useCallback, useMemo, useRef, useState } from 'react'
import {
  ClipboardIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  ArrowPathIcon,
  ClockIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { toast } from 'sonner'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import { buildTaggedUrl, type UtmKey, type UtmValues } from './buildTaggedUrl'
import { logClientError } from '~/lib/logger-client'

const RESOURCE_SLUG = 'utm-campaign-url-builder'
const HISTORY_LIMIT = 8

interface UtmFieldDef {
  key: UtmKey
  label: string
  helper: string
  placeholder: string
  required: boolean
}

const UTM_FIELDS: readonly UtmFieldDef[] = [
  {
    key: 'utm_source',
    label: 'Campaign Source',
    helper: 'Where the traffic comes from — the referrer (e.g. google, facebook, newsletter).',
    placeholder: 'google',
    required: true,
  },
  {
    key: 'utm_medium',
    label: 'Campaign Medium',
    helper: 'The marketing channel or type (e.g. cpc, paid_social, email, organic).',
    placeholder: 'cpc',
    required: true,
  },
  {
    key: 'utm_campaign',
    label: 'Campaign Name',
    helper: 'The specific promotion or strategic campaign (e.g. spring_sale_2026).',
    placeholder: 'spring_sale_2026',
    required: true,
  },
  {
    key: 'utm_term',
    label: 'Campaign Term',
    helper: 'Paid-search keyword for this link. Optional for most channels.',
    placeholder: 'running_shoes',
    required: false,
  },
  {
    key: 'utm_content',
    label: 'Campaign Content',
    helper: 'Differentiates ads/links pointing to the same URL (e.g. hero_cta, logo_link).',
    placeholder: 'hero_cta',
    required: false,
  },
] as const

interface PlatformPreset {
  id: string
  label: string
  source: string
  medium: string
}

/**
 * One-click channel presets. Each fills the source/medium pair with the
 * taxonomy GA4 reporting expects so cross-channel reports stay consistent.
 * `paid_social` (not `social` / `cpc`) is the modern convention for paid
 * placements on social platforms; `cpc` is reserved for search.
 */
const PLATFORM_PRESETS: readonly PlatformPreset[] = [
  { id: 'google-ads', label: 'Google Ads', source: 'google', medium: 'cpc' },
  { id: 'meta', label: 'Meta / Facebook', source: 'facebook', medium: 'paid_social' },
  { id: 'instagram', label: 'Instagram', source: 'instagram', medium: 'paid_social' },
  { id: 'tiktok', label: 'TikTok Ads', source: 'tiktok', medium: 'paid_social' },
  { id: 'linkedin', label: 'LinkedIn Ads', source: 'linkedin', medium: 'paid_social' },
  { id: 'x', label: 'X (Twitter) Ads', source: 'twitter', medium: 'paid_social' },
  { id: 'youtube', label: 'YouTube Ads', source: 'youtube', medium: 'video' },
  { id: 'microsoft', label: 'Microsoft / Bing', source: 'bing', medium: 'cpc' },
  { id: 'email', label: 'Email', source: 'newsletter', medium: 'email' },
] as const

const EMPTY_VALUES: UtmValues = {
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_term: '',
  utm_content: '',
}

interface HistoryEntry {
  url: string
  campaign: string
  at: number
}

export function UtmBuilder(): JSX.Element {
  const [baseUrl, setBaseUrl] = useState('')
  const [values, setValues] = useState<UtmValues>(EMPTY_VALUES)
  const [cleanValues, setCleanValues] = useState(true)
  const [activePreset, setActivePreset] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [history, setHistory] = useState<HistoryEntry[]>([])

  const [getTotalEngagementMs, getDeltaEngagementMs] = useMemo(() => createEngagementTimers(), [])
  const lastTrackedRef = useRef<string>('')

  const result = useMemo(
    () => buildTaggedUrl(baseUrl, values, cleanValues),
    [baseUrl, values, cleanValues],
  )

  const requiredMissing = UTM_FIELDS.filter(f => f.required && !values[f.key].trim())
  const hasBase = baseUrl.trim().length > 0
  const isComplete = hasBase && !result.error && requiredMissing.length === 0 && result.url.length > 0

  const updateValue = useCallback((key: UtmKey, value: string): void => {
    setValues(prev => ({ ...prev, [key]: value }))
    // Editing source/medium by hand detaches the active preset highlight.
    if (key === 'utm_source' || key === 'utm_medium') setActivePreset(null)
  }, [])

  const applyPreset = useCallback((preset: PlatformPreset): void => {
    setValues(prev => ({ ...prev, utm_source: preset.source, utm_medium: preset.medium }))
    setActivePreset(preset.id)
    sendResourceEvent('interact', {
      resourceType: 'tool',
      resourceSlug: RESOURCE_SLUG,
      engagementMs: getDeltaEngagementMs(),
      params: { tool_type: 'generator', interaction_type: 'apply_preset', preset: preset.id },
    })
  }, [getDeltaEngagementMs])

  // Recent links are kept in memory for the current session only — deliberately
  // NOT persisted to localStorage. A built URL can carry sensitive query params
  // (tokens/session IDs) the user pasted into the base URL, and writing those to
  // disk on a shared machine would contradict the tool's "nothing is stored"
  // promise. In-memory history gives the re-copy convenience without that risk.
  const recordHistory = useCallback((url: string): void => {
    if (!url || url === lastTrackedRef.current) return
    lastTrackedRef.current = url
    const entry: HistoryEntry = {
      url,
      campaign: values.utm_campaign.trim() || '(untitled campaign)',
      at: Date.now(),
    }
    setHistory(prev => [entry, ...prev.filter(h => h.url !== url)].slice(0, HISTORY_LIMIT))
  }, [values.utm_campaign])

  const copyUrl = useCallback(async (url: string, fromHistory = false): Promise<void> => {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      if (!fromHistory) {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 2000)
        recordHistory(url)
      }
      sendResourceEvent('complete', {
        resourceType: 'tool',
        resourceSlug: RESOURCE_SLUG,
        engagementMs: getTotalEngagementMs(),
        params: {
          tool_type: 'generator',
          interaction_type: fromHistory ? 'copy_history' : 'copy_url',
          outcome: 'success',
        },
      })
    } catch (error) {
      logClientError(error, { component: 'UtmBuilder', context: 'Failed to copy URL' })
      toast.error('Could not copy to clipboard. Please select the URL and copy it manually.')
    }
  }, [getTotalEngagementMs, recordHistory])

  const reset = useCallback((): void => {
    setBaseUrl('')
    setValues(EMPTY_VALUES)
    setActivePreset(null)
    setCopied(false)
  }, [])

  const clearHistory = useCallback((): void => {
    setHistory([])
  }, [])

  const inputClasses = (invalid: boolean): string =>
    clsx(
      'w-full rounded-lg border px-3 py-2 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500',
      'bg-white text-gray-900 dark:bg-gray-800 dark:text-white',
      invalid ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600',
    )

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-1 sm:px-0 lg:flex-row lg:items-start">
      {/* ── Controls ─────────────────────────────────────────────── */}
      <section className="flex-1 rounded-3xl border border-slate-200/70 bg-white/80 p-6 shadow-2xl dark:border-slate-800/70 dark:bg-slate-900/70">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Build your tracking URL</h2>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Add UTM parameters so Google Analytics, GA4, and your ad platform attribute every click to the right campaign.
          </p>
        </div>

        {/* Destination URL */}
        <div className="space-y-2">
          <label htmlFor="utm-base-url" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Website URL <span className="text-rose-500">*</span>
          </label>
          <div className="relative">
            <LinkIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-slate-400" aria-hidden="true" />
            <input
              id="utm-base-url"
              type="url"
              inputMode="url"
              value={baseUrl}
              onChange={e => setBaseUrl(e.target.value)}
              placeholder="https://www.example.com/landing-page"
              className={clsx(inputClasses(Boolean(result.error)), 'pl-10')}
              aria-invalid={Boolean(result.error)}
              aria-describedby={result.error ? 'utm-url-error' : undefined}
            />
          </div>
          {result.error ? (
            <p id="utm-url-error" className="text-sm text-rose-500 dark:text-rose-300">{result.error}</p>
          ) : (
            <p className="text-xs text-slate-500 dark:text-slate-400">The full destination, including any path. Existing query parameters are preserved.</p>
          )}
        </div>

        {/* Platform presets */}
        <div className="mt-6">
          <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">Quick presets</p>
          <div className="flex flex-wrap gap-2">
            {PLATFORM_PRESETS.map(preset => (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset)}
                aria-pressed={activePreset === preset.id}
                className={clsx(
                  'rounded-full border px-3 py-1.5 text-xs font-medium transition',
                  activePreset === preset.id
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400 dark:bg-indigo-500/15 dark:text-indigo-200'
                    : 'border-slate-300/70 text-slate-600 hover:border-indigo-300 hover:text-indigo-600 dark:border-slate-600 dark:text-slate-300 dark:hover:border-indigo-500/40',
                )}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* UTM fields */}
        <div className="mt-6 space-y-5">
          {UTM_FIELDS.map(field => {
            const missing = field.required && hasBase && !values[field.key].trim()
            return (
              <div key={field.key} className="space-y-1.5">
                <label htmlFor={`utm-${field.key}`} className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                  {field.label}
                  {field.required && <span className="ml-1 text-rose-500">*</span>}
                  <span className="ml-2 font-mono text-xs font-normal text-slate-400">{field.key}</span>
                </label>
                <input
                  id={`utm-${field.key}`}
                  type="text"
                  value={values[field.key]}
                  onChange={e => updateValue(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  className={inputClasses(missing)}
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">{field.helper}</p>
              </div>
            )
          })}
        </div>

        {/* Options */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200/70 pt-5 dark:border-slate-700/70">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={cleanValues}
              onChange={e => setCleanValues(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600"
            />
            Clean values (lowercase, spaces → underscores)
          </label>
          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1.5 rounded-full border border-slate-300/70 px-4 py-1.5 text-sm font-medium text-slate-700 transition hover:border-red-200 hover:text-red-600 dark:border-slate-600 dark:text-slate-200 dark:hover:border-red-500/40 dark:hover:text-red-200"
          >
            <ArrowPathIcon className="h-4 w-4" /> Reset
          </button>
        </div>
      </section>

      {/* ── Output ───────────────────────────────────────────────── */}
      <aside className="lg:sticky lg:top-24 lg:w-[24rem]">
        <div className="rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl dark:border-slate-700/70 dark:bg-slate-900/70">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">Your campaign URL</h3>

          {isComplete ? (
            <>
              <div className="mt-4 break-all rounded-xl bg-slate-900 p-4 font-mono text-sm text-emerald-300 dark:bg-black/40">
                {result.url}
              </div>
              <button
                type="button"
                onClick={() => void copyUrl(result.url)}
                className={clsx(
                  'mt-4 flex w-full items-center justify-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition',
                  copied ? 'bg-emerald-600' : 'bg-indigo-600 hover:bg-indigo-700',
                )}
              >
                {copied ? (
                  <><ClipboardDocumentCheckIcon className="h-5 w-5" /> Copied!</>
                ) : (
                  <><ClipboardIcon className="h-5 w-5" /> Copy URL</>
                )}
              </button>

              {result.warnings.length > 0 && (
                <ul className="mt-4 space-y-1.5">
                  {result.warnings.map(w => (
                    <li key={w} className="flex items-start gap-2 text-xs text-amber-600 dark:text-amber-400">
                      <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <p className="mt-4 rounded-xl border border-dashed border-slate-300/70 p-4 text-sm text-slate-500 dark:border-slate-600 dark:text-slate-400">
              {hasBase
                ? `Add the required parameter${requiredMissing.length > 1 ? 's' : ''}: ${requiredMissing.map(f => f.label).join(', ')}.`
                : 'Enter a website URL and at least a source, medium, and campaign name to generate a tracking link.'}
            </p>
          )}

          {/* Session history */}
          {history.length > 0 && (
            <div className="mt-6 border-t border-slate-200/70 pt-5 dark:border-slate-700/70">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
                  <ClockIcon className="h-4 w-4" /> Recent links
                </h4>
                <button
                  type="button"
                  onClick={clearHistory}
                  className="flex items-center gap-1 text-xs text-slate-400 transition hover:text-red-500"
                >
                  <TrashIcon className="h-3.5 w-3.5" /> Clear
                </button>
              </div>
              <ul className="space-y-2">
                {history.map(entry => (
                  <li key={entry.url} className="group flex items-center gap-2 rounded-lg border border-slate-200/70 p-2 text-xs dark:border-slate-700/70">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-700 dark:text-slate-200">{entry.campaign}</p>
                      <p className="truncate font-mono text-slate-400">{entry.url}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void copyUrl(entry.url, true)}
                      className="flex-shrink-0 rounded-md p-1.5 text-slate-400 transition hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-500/10"
                      aria-label={`Copy URL for ${entry.campaign}`}
                    >
                      <ClipboardIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-6 text-xs text-slate-400">
            Built entirely in your browser — nothing is uploaded to AdSights, and recent links are
            kept only for this browser session (never written to disk).
          </p>
        </div>
      </aside>
    </div>
  )
}
